import { FC, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import usePermissions from 'api/usePermissions';
import usePublisher from 'api/usePublisher';
import { EFeatureFlag, ESetting } from 'constants/enums';

import Button from 'components/Button/Button';
import { Modal } from 'components/Modal/Modal';
import { Flex } from 'components/ui/flex';

import { EToasterStatus, showToast } from '../../utils/showToast';

import ExchangeRatesUpdaterManager from './ExchangeRatesUpdaterManager';
import ExchangeRatesUpdaterManagerModal from './ExchangeRatesUpdaterManagerModal';
import ExchangeRateThreshold from './ExchangeRateThreshold';
import LocalizedCurrencies from './LocalizedCurrencies';
import PriceSettingToggle from './PriceSettingToggle';

import './style.scss';

export const PricingSettings: FC = () => {
  const { publisherId } = useParams();
  const { getPublisherSettings, updatePublisherSettings } = usePublisher(
    publisherId ? publisherId : undefined
  );
  const { featureFlags } = useFeatureFlags({ publisherId: publisherId });
  const { isSuperAdminByProject } = usePermissions();
  const [updateExchangeRatesDialogOpen, setUpdateExchangeRatesDialogOpen] =
    useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<ESetting | null>(null);
  const [settings, setSettings] = useState({
    roundingRulesEnabled: false,
    taxEnabled: false
  });
  const isSuperAdmin = isSuperAdminByProject(publisherId ?? '');

  const hasFeatureFlagPricePointAlerting =
    featureFlags?.[EFeatureFlag.DASHBOARD_PRICE_POINT_ALERTING];
  const hasFeatureFlagDisableCurrencies =
    featureFlags?.[EFeatureFlag.DASHBOARD_DISABLE_CURRENCIES];

  useEffect(() => {
    getPublisherSettings
      .refetch()
      .then((response) => {
        const settings = response.data;
        setSettings({
          roundingRulesEnabled: settings?.roundingOn,
          taxEnabled: settings?.taxOn
        });
      })
      .catch((error) => {
        showToast({
          message: 'Failed to load settings',
          status: EToasterStatus.ERROR
        });
        console.error('Error fetching publisher settings:', error);
      });
  }, []);

  const handleSettingChange = (settingType: ESetting) => {
    setDialogType(settingType);
    setDialogOpen(true);
  };

  const submitSettingChange = () => {
    if (!dialogType) return;
    const settingName =
      dialogType === ESetting.ROUNDING_ON ? 'Rounding rules' : 'Tax';
    const isActive =
      dialogType === ESetting.ROUNDING_ON
        ? !settings.roundingRulesEnabled
        : !settings.taxEnabled;
    updatePublisherSettings.mutate(
      { setting: dialogType, isActive },
      {
        onSuccess: () => {
          showToast({
            message: `${settingName} setting updated successfully`,
            status: EToasterStatus.SUCCESS
          });
          setSettings((prevSettings) => ({
            ...prevSettings,
            [dialogType === ESetting.ROUNDING_ON
              ? 'roundingRulesEnabled'
              : 'taxEnabled']: isActive
          }));
          setDialogOpen(false);
        },
        onError: () => {
          showToast({
            message: `Error updating ${settingName} setting`,
            status: EToasterStatus.ERROR
          });
          setDialogOpen(false);
        }
      }
    );
  };

  return (
    <Flex className="mb-20">
      {hasFeatureFlagDisableCurrencies && <LocalizedCurrencies />}
      <ExchangeRatesUpdaterManager
        setUpdateExchangeRatesDialogOpen={setUpdateExchangeRatesDialogOpen}
      />
      <PriceSettingToggle
        title="Display Tax-Inclusive Prices"
        description="Automatically include tax in local prices for countries where it’s required by law to show tax-inclusive pricing."
        action={() => handleSettingChange(ESetting.TAX_ON)}
        isOn={settings.taxEnabled}
        disabled={
          getPublisherSettings.isLoading ||
          getPublisherSettings.isFetching ||
          !isSuperAdmin
        }
      />
      <PriceSettingToggle
        title="Rounding Rules"
        description="Automatically round localized prices to .00, .09, .90 or .99."
        action={() => handleSettingChange(ESetting.ROUNDING_ON)}
        isOn={settings.roundingRulesEnabled}
        disabled={
          getPublisherSettings.isLoading ||
          getPublisherSettings.isFetching ||
          !isSuperAdmin
        }
      />
      {hasFeatureFlagPricePointAlerting && <ExchangeRateThreshold />}
      <Modal isOpen={isDialogOpen} onClose={() => setDialogOpen(false)}>
        <Flex className="w-full gap-4">
          <Flex className="gap-2">
            <h2 className="text-xl font-semibold">Changes Approval</h2>
            <p className="text-sm text-gray-600">
              Enabling this toggle will affect all existing price points. Do you
              approve?
            </p>
          </Flex>

          <Flex direction="row" justify="end" className="gap-2">
            <Button
              onClick={() => setDialogOpen(false)}
              variant="outline"
              type="button"
            >
              Cancel
            </Button>
            <Button
              onClick={submitSettingChange}
              variant="default"
              type="button"
            >
              Approve
            </Button>
          </Flex>
        </Flex>
      </Modal>
      <ExchangeRatesUpdaterManagerModal
        isOpen={updateExchangeRatesDialogOpen}
        setIsExchangeRateManagerModalOpen={setUpdateExchangeRatesDialogOpen}
      />
    </Flex>
  );
};

export default PricingSettings;
